import { MaterialCommunityIcons } from '@expo/vector-icons';
import {
  AssetFilterModel,
  extractRoutineStatusTypeValue,
  formatShortDateOrSameDay,
  resolveColumnsForWorkspace,
  RoutineType,
  useCurrentWorkspace,
  useSetAssetListFilter,
  useStatefulWorkspaceRoutines,
} from '@gripp/shared-logic';
import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Platform, StyleProp, StyleSheet, Text, View } from 'react-native';
import { Colors } from '../../themes/colors';
import { BaseColumnCheckbox } from '../baseColumnCheckbox';
import { getGroupColumn } from '../group';
import { WorkspaceColumn } from '../workspace';

const IconSize = 18;

const BaseColumns: Record<
  string,
  { element: (info: any) => JSX.Element; exportCell?: (info: any) => string }
> = {
  name: {
    element: (info) => getInfoLabel(styles.nameLabel, info),
  },
  description: {
    element: (info) => getInfoLabel(styles.greyLabel, info),
  },
  serialNumber: {
    element: (info) => getInfoLabel(styles.greyLabel, info),
  },
  productName: {
    element: (info) => getInfoLabel(styles.greyLabel, info),
  },
  lastUpdated: {
    element: (info) => getShortDateLabel(info),
  },
  lastScannedAt: {
    element: (info) => getShortDateLabel(info),
  },
  reports: {
    element: (info) => {
      const data = info.row.original;

      return (
        <View style={styles.iconRow}>
          <View style={[styles.iconContainer, { marginRight: 12 }]}>
            <MaterialCommunityIcons
              name="flag"
              color={Colors.red}
              size={IconSize}
              style={styles.icon}
            />
            {getLabel(styles.greyLabel, data.numReportsRed)}
          </View>

          <View style={styles.iconContainer}>
            <MaterialCommunityIcons
              name="flag-outline"
              color={Colors.yellow}
              size={IconSize}
              style={styles.icon}
            />
            {getLabel(styles.greyLabel, data.numReportsYellow)}
          </View>
        </View>
      );
    },
    exportCell: (data) =>
      `Red: ${data.numReportsRed}, Yellow: ${data.numReportsYellow}`,
  },
  routines: {
    element: (info) => {
      const data = info.row.original;

      return (
        <View style={styles.iconRow}>
          <MaterialCommunityIcons
            name="format-list-checks"
            color={Colors.blue}
            size={IconSize}
            style={styles.icon}
          />
          {getLabel(styles.greyLabel, data.numRoutines)}
        </View>
      );
    },
    exportCell: (data) => data.numRoutines,
  },
  addons: {
    element: (info) => {
      const data = info.row.original;
      return (
        <View style={styles.iconRow}>
          <MaterialCommunityIcons
            name="shape-outline"
            color={Colors.primary}
            size={IconSize}
            style={styles.icon}
          />
          {getLabel(styles.greyLabel, data.addonsCount ?? 0)}
        </View>
      );
    },
    exportCell: (data) => data.addonsCount ?? 0,
  },
  notes: {
    element: (info) => {
      const data = info.row.original;
      return (
        <View style={styles.iconRow}>
          <MaterialCommunityIcons
            name="note-plus"
            color={Colors.purple}
            size={IconSize}
            style={styles.icon}
          />
          {getLabel(styles.greyLabel, data.numNotes)}
        </View>
      );
    },
    exportCell: (data) => data.numNotes,
  },
  alerts: {
    element: (info) => {
      const data = info.row.original;
      return (
        <View style={styles.iconRow}>
          <MaterialCommunityIcons
            name="alarm-check"
            color={Colors.role.textStrong}
            size={IconSize}
            style={styles.icon}
          />
          {getLabel(styles.greyLabel, data.numAlerts)}
        </View>
      );
    },
    exportCell: (data) => data.numAlerts,
  },
  lastAddress: {
    element: (info) => getInfoLabel(styles.greyLabel, info),
  },
  tagCode: {
    element: (info) => getInfoLabel(styles.greyLabel, info),
  },
  createdAt: {
    element: (info) => getShortDateLabel(info),
  },
  createdBy: {
    element: (info) => getInfoLabel(styles.greyLabel, info),
  },
};

const getWorkspaceColumn = (info: any, canImpersonate: boolean) => {
  const workspaces = info.getValue();
  const workspace =
    workspaces && workspaces.length > 0 ? workspaces[0] : undefined;

  return (
    workspace && (
      <WorkspaceColumn
        canImpersonate={canImpersonate}
        workspaces={info.getValue() as any[]}
      />
    )
  );
};

export const useColumns = (
  canImpersonate: boolean,
  filter?: AssetFilterModel,
  routinesButton?: (data: any) => React.ReactElement,
  addonsButton?: (data: any) => React.ReactElement,
  alertsButton?: (data: {
    alertsCount: number;
    assetId: string;
    name: string;
  }) => React.ReactElement,
  groupsButton?: (data: {
    groups: any[];
    entityId: string;
    name: string;
  }) => React.ReactElement
) => {
  const [columns, setColumns] = useState<ColumnDef<unknown, never>[]>([]);
  const [exportColumns, setExportColumns] = useState<
    ColumnDef<unknown, never>[]
  >([]);
  const { mutateAsync: setAssetListFilter } = useSetAssetListFilter();

  const { t } = useTranslation();
  const columnHelper = createColumnHelper();
  const workspace = useCurrentWorkspace(true);
  const routines = useStatefulWorkspaceRoutines(workspace?.id);
  const isWeb = Platform.OS === 'web';

  const updateGroupFilter = async (groupId: string) => {
    await setAssetListFilter(
      new AssetFilterModel({
        ...filter,
        groups: [groupId],
      })
    );
  };

  const getRoutineExportValue = (routineState, routine) => {
    if (!routineState || !routine) return '';

    const state = routineState.find((x) => x.workspaceRoutineId === routine.id);

    let value;
    switch (routine.type) {
      case RoutineType.Counter: {
        const count = state?.state?.count;
        if (count !== undefined) {
          value = count;
        }
        break;
      }
      case RoutineType.Status:
        value = extractRoutineStatusTypeValue(state);
        break;
    }
    return value;
  };

  useEffect(() => {
    // Build a per-call local copy to avoid mutating shared BaseColumns across hook consumers
    const columnsConfig = { ...BaseColumns };

    // Interactive groups column (assignment modal)
    if (groupsButton) {
      columnsConfig['groups'] = {
        element: (info) => {
          const data = info.row.original;
          return groupsButton({
            groups: data.groups ?? [],
            entityId: data.id,
            name: data.name,
          });
        },
        exportCell: () => '',
      };
    }

    // Read-only group names display column
    columnsConfig['groupNames'] = {
      element: (info) => getGroupColumn(info, updateGroupFilter, true),
      exportCell: (data) =>
        data.groups?.map((group) => group.name).join(', ') || '',
    };

    columnsConfig['workspaces'] = {
      element: (info) => getWorkspaceColumn(info, canImpersonate),
      exportCell: (data) =>
        data.workspaces?.map((workspace) => workspace.name).join(', ') || '',
    };

    // Override routines column if routinesButton is provided
    if (routinesButton) {
      columnsConfig['routines'] = {
        element: (info) => {
          const data = info.row.original;
          return routinesButton({
            routinesCount: data.numRoutines,
            assetId: data.id,
            name: data.name,
          });
        },
        exportCell: (data) => data.numRoutines,
      };
    }

    // Override addons column if addonsButton is provided
    if (addonsButton) {
      columnsConfig['addons'] = {
        element: (info) => {
          const data = info.row.original;
          return addonsButton({
            addonsCount: data.addonsCount ?? 0,
            assetId: data.id,
            name: data.name,
          });
        },
        exportCell: (data) => data.addonsCount ?? 0,
      };
    }

    // Override alerts column if alertsButton is provided
    if (alertsButton) {
      columnsConfig['alerts'] = {
        element: (info) => {
          const data = info.row.original;
          return alertsButton({
            alertsCount: data.numAlerts ?? 0,
            assetId: data.id,
            name: data.name,
          });
        },
        exportCell: (data) => data.numAlerts ?? 0,
      };
    }
    if (!filter) return;

    // Resolve columns for workspace type and deduplicate
    let filteredColumns = filter.columns
      ? resolveColumnsForWorkspace(filter.columns, workspace?.type)
      : undefined;

    if (filteredColumns) {
      filteredColumns = Array.from(
        new Set(
          filteredColumns.filter(
            (c): c is string => c != null && typeof c === 'string'
          )
        )
      );
    }

    const updatedColumns: ColumnDef<unknown, never>[] = [];

    if (isWeb && workspace) {
      updatedColumns.push(
        createCheckboxColumn(
          'selected',
          BaseColumnCheckbox.checkbox,
          BaseColumnCheckbox.checkboxHeader
        )
      );
    }

    updatedColumns.push(createInfoColumn('name', columnsConfig.name.element));

    Object.entries(columnsConfig).forEach(([key, cell]) => {
      // Skip 'name' since it's already added above
      if (key === 'name') return;

      if (filteredColumns?.includes(key)) {
        const sortable = key !== 'tagCode';

        // groupNames reads from the 'groups' data field but needs a distinct column id
        if (key === 'groupNames') {
          updatedColumns.push(
            columnHelper.accessor('groups', {
              id: 'groupNames',
              cell: cell.element,
              header: () => t('assets.list.groupNames'),
              meta: {
                style: styles.cell,
                sortable: false,
                exportCell: cell.exportCell,
              },
            })
          );
        } else {
          updatedColumns.push(
            createInfoColumn(
              key,
              cell.element,
              undefined,
              cell.exportCell,
              sortable
            )
          );
        }
      }
    });

    if (routines) {
      routines.forEach((routine) => {
        if (![RoutineType.Counter, RoutineType.Status].includes(routine.type))
          return;

        if (filter.columns?.includes(`routine:${routine.id}`)) {
          updatedColumns.push(
            createInfoColumn(
              `routine:${routine.id}`,
              (info) => {
                const routineStates = info.row.original.routineState;
                if (!routineStates) return <></>;

                const routineState = info.row.original.routineState.find(
                  (x: any) => x.workspaceRoutineId === routine.id
                );
                let value;
                switch (routine.type) {
                  case RoutineType.Counter: {
                    const count = routineState?.state?.count;
                    if (count !== undefined) {
                      value = count;
                    }
                    break;
                  }
                  case RoutineType.Status:
                    value = extractRoutineStatusTypeValue(routineState);
                    break;
                }
                return getLabel(styles.greyLabel, value);
              },
              routine.config.title ?? routine.name,
              (data) => getRoutineExportValue(data.routineState, routine)
            )
          );
        }
      });
    }

    // Add split datetime columns for export
    if (filter.columns?.includes('createdAt')) {
      // Add createdDate column
      updatedColumns.push(
        createInfoColumn(
          'createdDate',
          () => null, // Hidden column for export only
          t('assets.list.createdDate'),
          (data: any) => {
            if (!data.createdAt) return '';
            return new Date(data.createdAt).toISOString().split('T')[0];
          },
          false, // Not sortable
          true // Hidden from grid
        )
      );

      // Add createdTime column
      updatedColumns.push(
        createInfoColumn(
          'createdTime',
          () => null, // Hidden column for export only
          t('assets.list.createdTime'),
          (data: any) => {
            if (!data.createdAt) return '';
            return new Date(data.createdAt)
              .toISOString()
              .split('T')[1]
              .substring(0, 8);
          },
          false, // Not sortable
          true // Hidden from grid
        )
      );
    }

    if (filter.columns?.includes('lastUpdated')) {
      // Add lastUpdatedDate column
      updatedColumns.push(
        createInfoColumn(
          'lastUpdatedDate',
          () => null, // Hidden column for export only
          t('assets.list.lastUpdatedDate'),
          (data: any) => {
            if (!data.lastUpdated) return '';
            return new Date(data.lastUpdated).toISOString().split('T')[0];
          },
          false, // Not sortable
          true // Hidden from grid
        )
      );

      // Add lastUpdatedTime column
      updatedColumns.push(
        createInfoColumn(
          'lastUpdatedTime',
          () => null, // Hidden column for export only
          t('assets.list.lastUpdatedTime'),
          (data: any) => {
            if (!data.lastUpdated) return '';
            return new Date(data.lastUpdated)
              .toISOString()
              .split('T')[1]
              .substring(0, 8);
          },
          false, // Not sortable
          true // Hidden from grid
        )
      );
    }

    if (filter.columns?.includes('lastScannedAt')) {
      // Add lastScannedDate column
      updatedColumns.push(
        createInfoColumn(
          'lastScannedDate',
          () => null, // Hidden column for export only
          t('assets.list.lastScannedDate'),
          (data: any) => {
            if (!data.lastScannedAt) return '';
            return new Date(data.lastScannedAt).toISOString().split('T')[0];
          },
          false, // Not sortable
          true // Hidden from grid
        )
      );

      // Add lastScannedTime column
      updatedColumns.push(
        createInfoColumn(
          'lastScannedTime',
          () => null, // Hidden column for export only
          t('assets.list.lastScannedTime'),
          (data: any) => {
            if (!data.lastScannedAt) return '';
            return new Date(data.lastScannedAt)
              .toISOString()
              .split('T')[1]
              .substring(0, 8);
          },
          false, // Not sortable
          true // Hidden from grid
        )
      );
    }

    // Keep full column list (including hidden/export-only) for CSV export
    setExportColumns(updatedColumns);

    // Filter out hidden columns from grid display but keep them for export
    const gridColumns = updatedColumns.filter(
      (column) => !(column.meta as any)?.hidden
    );
    setColumns(gridColumns);
  }, [
    filter,
    workspace,
    routines,
    isWeb,
    canImpersonate,
    routinesButton,
    addonsButton,
    alertsButton,
  ]);

  const createCheckboxColumn = (
    accessor: string,
    cell: (info: any) => JSX.Element,
    header: (info: any) => JSX.Element
  ) => {
    return columnHelper.accessor(accessor, {
      cell: cell,
      header: header,
      meta: { style: styles.checkboxCell },
    });
  };

  const createInfoColumn = (
    accessor: string,
    cell: (info: any) => JSX.Element,
    headerOverride?: string,
    exportCell?: (info: any) => string,
    sortable = true,
    hidden = false
  ) => {
    return columnHelper.accessor(accessor, {
      cell: cell,
      header: () => headerOverride ?? t(`assets.list.${accessor}`),
      meta: {
        style: styles.cell,
        sortable: sortable,
        exportCell: exportCell,
        hidden: hidden,
      },
    });
  };

  return { columns, exportColumns };
};

const getLabel = (labelStyle: StyleProp<any>, label: string) => {
  return <Text style={[labelStyle, styles.cellText]}>{label}</Text>;
};

const getInfoLabel = (labelStyle: StyleProp<any>, info: any) => {
  return getLabel(labelStyle, info.getValue());
};

const getShortDateLabel = (info: any) => {
  const value = info.getValue();
  const date = value ? formatShortDateOrSameDay(value) : '';

  return getLabel(styles.greyLabel, date);
};

const styles = StyleSheet.create({
  cell: {
    alignItems: 'center',
    justifyContent: 'flex-start',
    overflow: 'hidden',
    paddingVertical: 4,
    ...Platform.select({
      web: {
        height: 50,
        paddingRight: 16,
      },
      default: {
        height: 68,
        paddingRight: 12,
      },
    }),
  },
  checkboxCell: {
    maxWidth: 50,
    flexDirection: 'row',
    justifyContent: 'center',
  },
  nameLabel: {
    color: Colors.role.textStrong,
  },
  greyLabel: {
    color: Colors.role.textSecondary,
  },
  cellText: {
    fontSize: 16,
    lineHeight: 17,
    ...Platform.select({
      web: {
        fontWeight: '500',
        flexWrap: 'nowrap',
      },
      default: {
        fontWeight: '400',
        flexWrap: 'wrap',
      },
    }),
    color: Colors.role.textStrong,
  },
  iconContainer: {
    flexDirection: 'row',
  },
  icon: {
    marginRight: 2,
  },
  iconRow: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
    flex: 1,
  },
});
